import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  CreateProcessRequest,
  CreateProcessResponse,
  DefaultResponse,
  GetRedirectUrlResponse,
  ProcessingRequest,
  ProcessingResponse,
  TokenPairResponse,
} from '@codegen';
import { DefaultService } from '@codegen/service';
import { UpdateAuthorizationToken } from '@modules/authorization/store/authorization.action';
import { AppState } from '@modules/shared/store';
import { AuthorizationFinish } from '@modules/shared/store/common.actions';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { concatMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  private defaultService = inject(DefaultService);
  private store = inject<Store<AppState>>(Store);
  private router = inject(Router);

  public startRegistrationNewUser(): Observable<TokenPairResponse> {
    return this.defaultService.authStart();
  }

  public getRedirectUrl(): Observable<GetRedirectUrlResponse> {
    return this.defaultService.authOAuthRedirectUrl();
  }

  public proceedRegistration(): Observable<ProcessingResponse> {
    const code = localStorage.getItem('code');

    return this.defaultService.authOAuthProcessCode({ code }).pipe(
      concatMap(
        (
          res
        ):
          | Action[]
          | Observable<ProcessingResponse>
          | Observable<CreateProcessResponse> => {
          localStorage.removeItem('code');
          localStorage.removeItem('authState');
          if (res.errorInfo.error === '10100') {
            this.store.dispatch(new AuthorizationFinish());

            return [];
          } else if (res.errorInfo.error === '12000') {
            localStorage.removeItem('processId');
            this.router.navigate(['auth/authError'], {
              state: {
                missedDataTitle: res.missedDataTitle,
                missedDataDescription: res.missedDataDescription,
              },
            });
            return [];
          } else if (localStorage.getItem('processId')) {
            return this.defaultService.questionnaireProcess({
              processId: localStorage.getItem('processId'),
            });
          } else {
            return this.defaultService.questionnaireCreateProcess({
              questionnaireId:
                res.questionnaireId as CreateProcessRequest.QuestionnaireIdEnum,
            });
          }
        }
      ),
      concatMap((res: CreateProcessResponse) => {
        if (!localStorage.getItem('processId')) {
          localStorage.setItem('processId', res.processId);
          return this.defaultService.questionnaireProcess({
            processId: localStorage.getItem('processId'),
          });
        }
      })
    );
  }

  public updateAuthProcess(
    questionnaireId: string
  ): Observable<ProcessingResponse> {
    return this.defaultService
      .questionnaireCreateProcess({
        questionnaireId:
          questionnaireId as CreateProcessRequest.QuestionnaireIdEnum,
      })
      .pipe(
        concatMap(res => {
          localStorage.setItem('processId', res.processId);
          return this.defaultService.questionnaireProcess({
            processId: localStorage.getItem('processId'),
          });
        })
      );
  }

  public refreshAuthorizationToken(): void {
    this.store.dispatch(new UpdateAuthorizationToken());
  }

  public logoutCurrentUser(): Observable<DefaultResponse> {
    return this.defaultService.authLogout();
  }

  public changeQuestionnaireProcessStep(
    processId: string,
    action: string,
    questionMap: { [key: string]: string },
    confirmationData?: string
  ): Observable<ProcessingResponse> {
    return this.defaultService.questionnaireProcess({
      processId,
      action: action as ProcessingRequest.ActionEnum,
      questionMap,
      confirmationData,
    });
  }

  public processCode(): Observable<ProcessingResponse> {
    return this.defaultService.questionnaireProcess({
      processId: localStorage.getItem('processId'),
    });
  }

  public finishAuth(): Observable<TokenPairResponse> {
    return this.defaultService.authFinish();
  }

  public removeCookie(cookieName: string): void {
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; path=/;`;
  }

  public isAuthorized(): boolean {
    return localStorage.getItem('authorized') === 'true';
  }
}
